import { useMemo, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  Bell,
  CalendarClock,
  FileText,
  Handshake,
  Info,
  type LucideIcon,
  PieChart,
  ShieldCheck,
  TrendingUp,
  User,
} from 'lucide-react'
import type { CustomDataArtifact } from '@dev-streetbeat/chat/types'

import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AllocationAggregationMenu,
  type AllocationAggregationId,
} from '@/components/credem/AllocationAggregationMenu'

import styles from './ClientBriefArtifact.module.scss'
import { BriefChart, type BriefChartSpec, parseChart } from './BriefCharts'

// chat-ui's ArtifactProps type isn't re-exported from the package root, so
// redeclare the minimal runtime contract.
interface ArtifactProps<T> {
  artifact: T
  onLoaded?: () => void
}

/**
 * Renderer for the credem.client_brief artifact (v1 and v2).
 *
 * v1 schema (legacy, render_client_brief_artifact tool):
 *   { client_code, client_label, as_of_dt,
 *     sections: [{ key, title, as_of_dt, as_of_note, rows: [{label, value}] }],
 *     unavailable_sections: string[],
 *     source_summary: string }
 *
 * v2 schema (current, client_brief_get tool):
 *   Same as v1, plus:
 *   - sections[].available?: boolean — when false the rows carry a
 *     "Dato non disponibile" placeholder generated server-side.
 *   - sections[].chart?: ChartSpec | null — optional chart per section
 *     (donut for composition, bar for performance, grouped_bar for
 *     concentration_drift). `timeline` payloads from the backend are
 *     parsed to null and the section falls back to row data only.
 *
 * v2 is rendered exactly like v1 (rows + as_of_note + header source tooltip)
 * plus the chart below the rows where present. v1 payloads parse cleanly with the
 * v2 reader since `chart` is optional.
 */

interface BriefRow {
  label: string
  value: string
}

interface BriefSection {
  key: string
  title: string
  as_of_dt: string
  as_of_note: string
  available: boolean
  rows: BriefRow[]
  chart: BriefChartSpec | null
}

interface BriefPayload {
  client_code: string
  client_label: string
  as_of_dt: string
  sections: BriefSection[]
  unavailable_sections: string[]
  source_summary: string
}

// Map of known section keys to lucide icons. Unknown keys fall back to
// `FileText` so the visual rhythm stays consistent even if the backend adds
// a new section we haven't met yet.
const SECTION_ICONS: Record<string, LucideIcon> = {
  summary: User,
  composition: PieChart,
  performance: TrendingUp,
  events: Activity,
  flows_maturities: CalendarClock,
  mifid_status: ShieldCheck,
  concentration_drift: AlertTriangle,
  alerts_qc: Bell,
  relationship: Handshake,
  context: Info,
}

function iconForSection(key: string): LucideIcon {
  return SECTION_ICONS[key] ?? FileText
}

function parseRows(raw: unknown): BriefRow[] {
  if (!Array.isArray(raw)) return []
  const rows: BriefRow[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const entry = item as Record<string, unknown>
    const label = typeof entry.label === 'string' ? entry.label.trim() : ''
    const value = typeof entry.value === 'string' ? entry.value.trim() : ''
    if (!label || !value) continue
    rows.push({ label, value })
  }
  return rows
}

function parseSections(raw: unknown): BriefSection[] {
  if (!Array.isArray(raw)) return []
  const sections: BriefSection[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const entry = item as Record<string, unknown>
    const key = typeof entry.key === 'string' ? entry.key.trim() : ''
    const title = typeof entry.title === 'string' ? entry.title.trim() : ''
    if (!key || !title) continue
    // `available` is v2-only; v1 payloads always inferred "available" by
    // the presence of rows. Default to true so v1 brief still renders the
    // section header.
    const available = entry.available === undefined ? true : Boolean(entry.available)
    sections.push({
      key,
      title,
      as_of_dt: typeof entry.as_of_dt === 'string' ? entry.as_of_dt.trim() : '',
      as_of_note: typeof entry.as_of_note === 'string' ? entry.as_of_note.trim() : '',
      available,
      rows: parseRows(entry.rows),
      chart: parseChart(entry.chart),
    })
  }
  return sections
}

function parseStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const item of raw) {
    if (typeof item !== 'string') continue
    const trimmed = item.trim()
    if (trimmed) out.push(trimmed)
  }
  return out
}

function parsePayload(data: unknown): BriefPayload | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  const client_code = typeof raw.client_code === 'string' ? raw.client_code.trim() : ''
  if (!client_code) return null
  return {
    client_code,
    client_label: typeof raw.client_label === 'string' ? raw.client_label.trim() : '',
    as_of_dt: typeof raw.as_of_dt === 'string' ? raw.as_of_dt.trim() : '',
    sections: parseSections(raw.sections),
    unavailable_sections: parseStringList(raw.unavailable_sections),
    source_summary: typeof raw.source_summary === 'string' ? raw.source_summary.trim() : '',
  }
}

function formatAsOf(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y}`
  }
  return iso
}

export function ClientBriefArtifact({ artifact }: ArtifactProps<CustomDataArtifact>) {
  const payload = useMemo(() => parsePayload(artifact.custom_data?.data), [artifact])
  // Decorative aggregation pick for the composition donut. Only
  // `asset_class` is wired today; the menu advertises upcoming feeds. Lifted
  // here because the brief carries at most one composition section.
  const [compositionAggregation, setCompositionAggregation] =
    useState<AllocationAggregationId>('asset_class')
  if (!payload) return null
  if (payload.sections.length === 0 && payload.unavailable_sections.length === 0) return null

  const sectionCount = payload.sections.length
  const cardTitle = payload.client_label || `Cliente ${payload.client_code}`
  const artifactAsOf = payload.as_of_dt ? formatAsOf(payload.as_of_dt) : ''
  const hasSourceInfo = Boolean(payload.source_summary || artifactAsOf)

  return (
    <section
      className={styles.card}
      aria-label={`Brief ${cardTitle}`}
      data-ftue-step="brief-artifact"
    >
      <header className={styles.header}>
        <div className={styles.clientBlock}>
          <h3 className={styles.clientLabel}>{cardTitle}</h3>
          {payload.client_code && payload.client_label !== payload.client_code ? (
            <span className={styles.clientCode}>Codice cliente {payload.client_code}</span>
          ) : null}
        </div>
        <TooltipProvider>
          <div className={styles.headerMeta}>
            {hasSourceInfo ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" tabIndex={0} className="cursor-help">
                    <Info aria-hidden />
                    Fonti
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="end"
                  className="w-[min(320px,calc(100vw-2rem))] flex-col items-start gap-1.5"
                >
                  {payload.source_summary ? (
                    <span>
                      <span className="font-semibold">Fonte:</span>{' '}
                      <code className="break-words font-mono text-[11px] leading-relaxed">
                        {payload.source_summary}
                      </code>
                    </span>
                  ) : null}
                  {artifactAsOf ? (
                    <span>
                      <span className="font-semibold">Data di validità:</span> {artifactAsOf}
                    </span>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {sectionCount > 0 || payload.unavailable_sections.length > 0 ? (
              payload.unavailable_sections.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" tabIndex={0} className="cursor-help">
                      {`${sectionCount} ${sectionCount === 1 ? 'sezione' : 'sezioni'}`}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    className="w-[min(240px,calc(100vw-2rem))] flex-col items-start gap-1"
                  >
                    <span className={styles.unavailableTitle}>Sezioni non disponibili</span>
                    <ul className={styles.unavailableList}>
                      {payload.unavailable_sections.map((reason, idx) => (
                        <li key={`u-${idx}`} className={styles.unavailableItem}>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Badge variant="outline">
                  {`${sectionCount} ${sectionCount === 1 ? 'sezione' : 'sezioni'}`}
                </Badge>
              )
            ) : null}
          </div>
        </TooltipProvider>
      </header>

      {payload.sections.length > 0 ? (
        <div className={styles.sections}>
          {payload.sections.map((section) => {
            const Icon = iconForSection(section.key)
            const sectionAsOf = section.as_of_dt ? formatAsOf(section.as_of_dt) : ''
            // The composition section gets the aggregation picker in the
            // header right slot (mirroring the cliente detail allocation
            // card). To keep the header to a single row, the "al DD/MM/YYYY"
            // caption folds into the note line below; if no note exists it
            // becomes the note. Cash-only clients have no donut chart, so
            // they fall back to the default header layout.
            const showAggregationMenu =
              section.key === 'composition' &&
              section.available &&
              section.chart?.type === 'donut'
            const noteParts: string[] = []
            if (showAggregationMenu && sectionAsOf) noteParts.push(`al ${sectionAsOf}`)
            if (section.as_of_note) noteParts.push(section.as_of_note)
            const noteText = noteParts.join(' · ')
            return (
              <section key={section.key} className={styles.section} aria-label={section.title}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitleGroup}>
                    <span className={styles.sectionIcon} aria-hidden>
                      <Icon />
                    </span>
                    <h4 className={styles.sectionTitle}>{section.title}</h4>
                  </div>
                  {showAggregationMenu ? (
                    <AllocationAggregationMenu
                      aggregation={compositionAggregation}
                      onChange={setCompositionAggregation}
                    />
                  ) : sectionAsOf ? (
                    <span className={styles.sectionAsOf}>al {sectionAsOf}</span>
                  ) : null}
                </div>
                {noteText ? (
                  <p className={styles.sectionNote}>{noteText}</p>
                ) : null}
                {section.rows.length > 0 ? (
                  <dl className={styles.rows}>
                    {section.rows.map((row, idx) => (
                      <div key={`${section.key}-${idx}`} className={styles.row}>
                        <dt className={styles.rowLabel}>{row.label}</dt>
                        <dd className={styles.rowValue}>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className={styles.emptyRows}>Nessun dato disponibile per questa sezione.</p>
                )}
                {section.chart && section.available ? (
                  <div className={styles.chart}>
                    <BriefChart chart={section.chart} />
                  </div>
                ) : null}
              </section>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}
